import React, { useEffect, useMemo, useState } from "react"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Separator } from "@/components/ui/separator"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  ChevronRight,
  ClipboardList,
  HeartPulse,
  Phone,
  ShieldCheck,
  Sparkles,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { AgentTypeCard } from "./AgentTypeCard"
import type {
  DemoCallOptions,
  DemoCallRequest,
  DemoStrategyType,
} from "../types"

interface DemoCallComposerProps {
  options: DemoCallOptions
  submitting: boolean
  onSubmit: (request: DemoCallRequest) => void
}

const DEFAULT_PERSONA = {
  first_name: "Jane",
  last_name: "Doe",
  date_of_birth: "1950-04-12",
  provider_name: "Dr. Pear",
  practice_name: "",
}

const DEFAULT_CARE_PLAN = {
  conditions: "Hypertension\nType 2 Diabetes",
  medications: "Lisinopril 10mg daily\nMetformin 500mg twice daily",
  goals:
    "Keep blood pressure below 130/80\nWalk 20 minutes at least 4 days per week",
}

const AGENT_COPY: Record<
  DemoStrategyType,
  { label: string; description: string; icon: typeof ShieldCheck }
> = {
  CONSENT: {
    label: "Consent call",
    description: "Introduces the program and asks the patient to enroll.",
    icon: ShieldCheck,
  },
  CHECK_IN: {
    label: "Check-in call",
    description: "Walks an enrolled patient through their monthly care plan.",
    icon: HeartPulse,
  },
}

const toLines = (value: string) =>
  value
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)

/** Digits-only US formatting as you type, kept permissive for other regions. */
function formatPhoneInput(raw: string): string {
  const trimmed = raw.replace(/[^\d+]/g, "")
  const digits = trimmed.replace(/\D/g, "")
  if (!trimmed.startsWith("+") && digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  return raw
}

/** What the API wants: E.164. */
function toE164(raw: string): string {
  const digits = raw.replace(/\D/g, "")
  if (raw.trim().startsWith("+")) return `+${digits}`
  if (digits.length === 10) return `+1${digits}`
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`
  return raw.trim()
}

export function DemoCallComposer({
  options,
  submitting,
  onSubmit,
}: DemoCallComposerProps) {
  const consentAgent = options.agents.find((a) => a.strategy_type === "CONSENT")
  const checkinAgent = options.agents.find((a) => a.strategy_type === "CHECK_IN")

  const [agentType, setAgentType] = useState<DemoStrategyType>(
    consentAgent ? "CONSENT" : "CHECK_IN"
  )
  const [phone, setPhone] = useState("")
  const [persona, setPersona] = useState(DEFAULT_PERSONA)
  const [carePlan, setCarePlan] = useState(DEFAULT_CARE_PLAN)
  const [callerId, setCallerId] = useState<string>("")
  const [language, setLanguage] = useState<string>("english")
  const [voicemail, setVoicemail] = useState("")
  const [refreshScript, setRefreshScript] = useState(false)
  const [advancedOpen, setAdvancedOpen] = useState(false)

  useEffect(() => {
    if (!callerId && options.default_caller_id) {
      setCallerId(options.default_caller_id)
    }
  }, [callerId, options.default_caller_id])

  const selectedAgent = agentType === "CONSENT" ? consentAgent : checkinAgent
  const isCheckin = agentType === "CHECK_IN"

  const phoneReady = toE164(phone).replace(/\D/g, "").length >= 10
  const canSubmit = !!selectedAgent && !!callerId && phoneReady && !submitting

  const submitLabel = useMemo(() => {
    if (!submitting) return "Call now"
    return isCheckin ? "Preparing the call…" : "Dialing…"
  }, [submitting, isCheckin])

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    if (!selectedAgent || !canSubmit) return
    onSubmit({
      strategy_version_id: selectedAgent.strategy_version_id,
      verified_phone_number_id: callerId,
      to_number: toE164(phone),
      persona: {
        first_name: persona.first_name.trim() || "Jane",
        last_name: persona.last_name.trim() || "Doe",
        date_of_birth: persona.date_of_birth,
        provider_name: persona.provider_name.trim() || "Dr. Pear",
        practice_name: persona.practice_name.trim() || null,
      },
      care_plan: {
        conditions: toLines(carePlan.conditions),
        medications: toLines(carePlan.medications),
        goals: toLines(carePlan.goals),
      },
      language,
      voicemail_message: voicemail.trim() || null,
      refresh_script: refreshScript,
    })
  }

  return (
    <form onSubmit={handleSubmit}>
      <Card>
        <CardHeader>
          <CardTitle>New demo call</CardTitle>
          <CardDescription>
            Dials a real Pear agent to any phone. Nothing here touches client
            data — demo patients and care plans live in{" "}
            {options.organization_name}.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* 1 — what kind of call */}
          <div className="space-y-3">
            <Label>What should the agent do?</Label>
            <div className="grid gap-3 sm:grid-cols-2">
              {(["CONSENT", "CHECK_IN"] as DemoStrategyType[]).map((type) => {
                const agent = type === "CONSENT" ? consentAgent : checkinAgent
                const copy = AGENT_COPY[type]
                return (
                  <AgentTypeCard
                    key={type}
                    icon={copy.icon}
                    label={copy.label}
                    description={copy.description}
                    selected={agentType === type}
                    disabled={!agent}
                    disabledReason={
                      agent
                        ? undefined
                        : `No published ${copy.label.toLowerCase()} agent in ${
                            options.organization_name
                          }.`
                    }
                    onSelect={() => setAgentType(type)}
                  />
                )
              })}
            </div>
          </div>

          {/* 2 — who to dial */}
          <div className="space-y-2">
            <Label htmlFor="demo-phone">Phone number to call</Label>
            <div className="relative">
              <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="demo-phone"
                value={phone}
                onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
                placeholder="(555) 123-4567"
                autoComplete="off"
                inputMode="tel"
                className="h-11 pl-9 text-base"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              US numbers are assumed; prefix with + for anywhere else.
            </p>
          </div>

          <Separator />

          {/* 3 — the fiction the agent believes */}
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Who the agent thinks it's calling</Label>
              <p className="text-xs text-muted-foreground">
                The agent will verify this date of birth on the call — have it
                handy.
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="demo-first" className="text-xs font-normal text-muted-foreground">
                  First name
                </Label>
                <Input
                  id="demo-first"
                  value={persona.first_name}
                  onChange={(e) =>
                    setPersona({ ...persona, first_name: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="demo-last" className="text-xs font-normal text-muted-foreground">
                  Last name
                </Label>
                <Input
                  id="demo-last"
                  value={persona.last_name}
                  onChange={(e) =>
                    setPersona({ ...persona, last_name: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="demo-dob" className="text-xs font-normal text-muted-foreground">
                  Date of birth
                </Label>
                <Input
                  id="demo-dob"
                  type="date"
                  value={persona.date_of_birth}
                  onChange={(e) =>
                    setPersona({ ...persona, date_of_birth: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="demo-provider" className="text-xs font-normal text-muted-foreground">
                  Provider
                </Label>
                <Input
                  id="demo-provider"
                  value={persona.provider_name}
                  onChange={(e) =>
                    setPersona({ ...persona, provider_name: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="demo-practice" className="text-xs font-normal text-muted-foreground">
                  Practice name
                </Label>
                <Input
                  id="demo-practice"
                  value={persona.practice_name}
                  onChange={(e) =>
                    setPersona({ ...persona, practice_name: e.target.value })
                  }
                  placeholder={options.organization_name}
                />
                <p className="text-xs text-muted-foreground">
                  Set this to the prospect's practice and the agent will name it
                  on the call.
                </p>
              </div>
            </div>
          </div>

          {/* 4 — check-in only: what the agent talks about */}
          {isCheckin && (
            <>
              <Separator />
              <div className="space-y-3">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                  <div className="space-y-1">
                    <Label className="flex items-center gap-1.5">
                      <ClipboardList className="h-4 w-4 text-muted-foreground" />
                      Demo care plan
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      One item per line. The agent writes its script from this,
                      so the first call with a new plan takes about 30 seconds
                      to prepare.
                    </p>
                  </div>
                </div>
                <div className="grid gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="demo-conditions" className="text-xs font-normal text-muted-foreground">
                      Conditions
                    </Label>
                    <Textarea
                      id="demo-conditions"
                      rows={2}
                      value={carePlan.conditions}
                      onChange={(e) =>
                        setCarePlan({ ...carePlan, conditions: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="demo-meds" className="text-xs font-normal text-muted-foreground">
                      Medications
                    </Label>
                    <Textarea
                      id="demo-meds"
                      rows={2}
                      value={carePlan.medications}
                      onChange={(e) =>
                        setCarePlan({ ...carePlan, medications: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="demo-goals" className="text-xs font-normal text-muted-foreground">
                      Goals
                    </Label>
                    <Textarea
                      id="demo-goals"
                      rows={2}
                      value={carePlan.goals}
                      onChange={(e) =>
                        setCarePlan({ ...carePlan, goals: e.target.value })
                      }
                    />
                  </div>
                </div>
                <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={refreshScript}
                    onChange={(e) => setRefreshScript(e.target.checked)}
                    className="h-3.5 w-3.5 rounded border-input accent-pear-teal"
                  />
                  <Sparkles className="h-3.5 w-3.5" />
                  Rewrite the script even if this plan hasn't changed
                </label>
              </div>
            </>
          )}

          {/* 5 — the knobs most demos never touch */}
          <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="flex w-full items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                <ChevronRight
                  className={cn(
                    "h-4 w-4 transition-transform",
                    advancedOpen && "rotate-90"
                  )}
                />
                Caller ID, language and voicemail
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-3 pt-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-xs font-normal text-muted-foreground">
                    Call from
                  </Label>
                  <Select value={callerId} onValueChange={setCallerId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a number" />
                    </SelectTrigger>
                    <SelectContent>
                      {options.caller_ids.map((n) => (
                        <SelectItem key={n.id} value={n.id}>
                          {n.phone_number}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-normal text-muted-foreground">
                    Language
                  </Label>
                  <Select value={language} onValueChange={setLanguage}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {options.languages.map((lang) => (
                        <SelectItem key={lang} value={lang}>
                          {lang.charAt(0).toUpperCase() + lang.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="demo-voicemail" className="text-xs font-normal text-muted-foreground">
                  Voicemail message
                </Label>
                <Textarea
                  id="demo-voicemail"
                  rows={2}
                  value={voicemail}
                  onChange={(e) => setVoicemail(e.target.value)}
                  placeholder="Hi, this is a demo call from Pear Health. No action needed. Goodbye."
                />
              </div>
            </CollapsibleContent>
          </Collapsible>

          <div className="flex flex-wrap items-center gap-3 pt-1">
            <Button
              type="submit"
              size="lg"
              disabled={!canSubmit}
              className="gap-2"
            >
              <Phone className="h-4 w-4" />
              {submitLabel}
            </Button>
            {!options.caller_ids.length && (
              <span className="text-sm text-destructive">
                No verified caller ID in {options.organization_name}.
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </form>
  )
}
